export class InvalidTimestampError extends Error {
  constructor(readonly input: string) {
    super(`Not an RFC 3339 timestamp: ${JSON.stringify(input)}`);
    this.name = 'InvalidTimestampError';
  }
}

// Fix round 2 (calendar Task 2): the fractional-seconds group used to be
// non-capturing (\.\d+ with no parens) -- matched, then thrown away. Any
// input carrying milliseconds (which is every `Date.prototype.toISOString()`
// output, the exact string every JS caller in this codebase produces) was
// silently rounded down to the whole second, up to 999ms early, with no
// error. That is reachable from real operator input through
// `scheduleVariant` (packages/application/src/schedule/schedule-variant.ts),
// used by both `POST /api/variants/:id/schedule` and the MCP
// `schedule_variant` tool -- an operator confirming a reschedule time could
// have a different instant saved than the one the confirmation dialog showed
// them, which is exactly what spec section 9.4's before/after summary exists
// to prevent. Fixed by capturing group 7 below and threading it through to
// `Date.UTC`'s milliseconds parameter instead of dropping it.
const RFC3339 =
  /^(\d{4})-(\d{2})-(\d{2})[Tt ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?(Z|z|[+-]\d{2}:\d{2})?$/;

export const normalizeToUtc = (input: string): Date => {
  const m = RFC3339.exec(input.trim());
  if (!m) throw new InvalidTimestampError(input);

  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  const hour = Number(m[4]), minute = Number(m[5]), second = Number(m[6] ?? '0');
  const fraction = m[7];
  const zone = m[8];

  if ([year, month, day, hour, minute, second].some(Number.isNaN)) {
    throw new InvalidTimestampError(input);
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 ||
      hour > 23 || minute > 59 || second > 59) {
    throw new InvalidTimestampError(input);
  }

  // Preserve, don't reject: `Date` itself has no finer resolution than a
  // millisecond, and every JS caller that ever produces this input
  // (`Date.prototype.toISOString()`) emits exactly three fractional digits
  // -- rejecting sub-second input would make that output unusable as an
  // input here, for no benefit. Right-padded to 3 digits so '.5' means 500ms
  // (half a second), not 5ms, matching RFC 3339's own semantics for a
  // fractional-seconds value; digits beyond the third are truncated, not
  // rounded, which only matters for a precision `Date` cannot represent
  // anyway.
  const milliseconds = fraction ? Number(`${fraction}000`.slice(0, 3)) : 0;

  // Build the instant from the parsed fields rather than handing the string to
  // Date. Engine ISO parsing differs on out-of-range values, and Date.UTC rolls
  // over silently — 2026-02-30 becomes 2026-03-02 — so the constructed instant is
  // checked back against the input fields. A schedule that quietly moved two days
  // is worse than one that was rejected.
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second, milliseconds);
  const check = new Date(asUtc);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() + 1 !== month ||
      check.getUTCDate() !== day) {
    throw new InvalidTimestampError(input);
  }

  // No zone designator means UTC, per the product design.
  let offsetMinutes = 0;
  if (zone && zone !== 'Z' && zone !== 'z') {
    const offsetHour = Number(zone.slice(1, 3));
    const offsetMinute = Number(zone.slice(4, 6));
    // RFC 3339 bounds an offset the same way it bounds a time: hours 00-23,
    // minutes 00-59. The date fields above are range-checked; the offset must be
    // too. Without this, "+25:00" parses happily and shifts the instant a day.
    if (offsetHour > 23 || offsetMinute > 59) throw new InvalidTimestampError(input);
    const sign = zone.startsWith('-') ? -1 : 1;
    offsetMinutes = sign * (offsetHour * 60 + offsetMinute);
  }
  return new Date(asUtc - offsetMinutes * 60_000);
};

// Deliberately truncates to whole seconds for display -- a human should
// never see "14:00:00.000Z" in a confirmation dialog or a list row, and
// almost every instant that ever reaches this function has no sub-second
// component to lose anyway (nobody schedules to the millisecond by hand).
//
// This is now an intentional asymmetry, not an oversight: `normalizeToUtc`
// (above) and `Schedule.publishAt` preserve full millisecond precision, but
// this function's *output* never carries it back out. DO NOT "fix" that by
// making this emit milliseconds -- and do not treat this function's output
// as a round-trippable source of truth for a value you are not changing.
// Concretely: a reschedule/edit flow must build its new instant from values
// the operator actually edited (the picked day/time), never by re-parsing
// this function's truncated string as if it exactly represented an
// untouched field -- that reconstructs a *different*, truncated instant and
// silently loses whatever sub-second precision the original had, which is
// the exact bug class `normalizeToUtc`'s own fix above exists to prevent,
// reintroduced through the display path instead of the input path. See
// apps/web/src/components/SchedulePanel.tsx's `onSave` for the existing
// correct shape: it sends a freshly-built RFC 3339 string from the
// browser's own local input, never a value derived from a displayed
// `toIsoUtc` string. (Recorded in more detail in the calendar feature's own
// SDD ledger, .superpowers/sdd/2026-08-22-calendar/progress.md, Task 2's
// "OPEN DECISION carried to Task 5".)
export const toIsoUtc = (d: Date): string => `${d.toISOString().slice(0, 19)}Z`;

export const isInPast = (instant: Date, now: Date): boolean => instant.getTime() < now.getTime();
